import { Alert, Button, Card, FormField, Input, SegmentedControl, Select } from '@ashniva/ui';
import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';

import { useTasksQuery } from '../../tasks/api';
import { useRequestAnalysis, type AnalysisSource } from '../api-changes';
import { classifyProblem } from '../ri-problems';
import { useSubmissionKey } from '../use-submission-key';
import { useWorkspace } from '../workspace-context';
import { AnalysisView } from './AnalysisView';
import { ProblemNotice } from './ProblemNotice';
import { RecentAnalyses } from './RecentAnalyses';

type Source = AnalysisSource['source'];

const SOURCES: Array<{ key: Source; label: string }> = [
  { key: 'commit_range', label: 'Commits' },
  { key: 'pull_request', label: 'Pull request' },
  { key: 'task', label: 'A task’s commits' },
];

/**
 * Change analysis (P14-07): pick what to analyse — two commits or branches, a pull request, or the commits linked to
 * one of the project's tasks — then follow it and read the results. The open analysis's id is in the URL.
 */
export function ChangesPanel() {
  const { linkId, projectId } = useWorkspace();
  const [params, setParams] = useSearchParams();
  const analysisId = params.get('analysis');
  const [source, setSource] = useState<Source>('commit_range');
  const [base, setBase] = useState('');
  const [head, setHead] = useState('');
  const [prNumber, setPrNumber] = useState('');
  const [taskId, setTaskId] = useState('');
  const tasks = useTasksQuery({ view: 'all', projectId }, source === 'task');
  const request = useRequestAnalysis(linkId);
  const keys = useSubmissionKey();

  const input = ((): AnalysisSource | null => {
    if (source === 'commit_range') {
      return base.trim() && head.trim() ? { source, base: base.trim(), head: head.trim() } : null;
    }
    if (source === 'pull_request') {
      const number = Number(prNumber);
      return Number.isInteger(number) && number > 0 ? { source, prNumber: number } : null;
    }
    return taskId ? { source, taskId } : null;
  })();

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    // One submission at a time: a second click while the first is on its way sends nothing.
    if (!input || request.isPending) {
      return;
    }
    const clientRequestId = keys.start(input);
    if (!clientRequestId) {
      return;
    }
    request.mutate(
      { ...input, clientRequestId },
      keys.callbacks((receipt: { analysis: { id: string } }) =>
        setParams((current) => {
          const next = new URLSearchParams(current);
          next.set('analysis', receipt.analysis.id);
          return next;
        }),
      ),
    );
  };

  return (
    <div className="ri-stack">
      <Card title="Analyse a change">
        <form className="ri-form" onSubmit={onSubmit} aria-label="Analyse a change">
          <SegmentedControl
            aria-label="What to analyse"
            options={SOURCES}
            value={source}
            onChange={setSource}
          />
          {source === 'commit_range' ? (
            <div className="ri-row">
              <FormField label="From (base)" hint="A branch, tag or commit, such as main">
                <Input
                  value={base}
                  maxLength={200}
                  onChange={(event) => setBase(event.target.value)}
                />
              </FormField>
              <FormField label="To (head)" hint="The branch or commit with the change">
                <Input
                  value={head}
                  maxLength={200}
                  onChange={(event) => setHead(event.target.value)}
                />
              </FormField>
            </div>
          ) : null}
          {source === 'pull_request' ? (
            <FormField
              label="Pull request number"
              hint="A pull request of this repository, not of a fork"
            >
              <Input
                inputMode="numeric"
                value={prNumber}
                onChange={(event) => setPrNumber(event.target.value.replace(/\D/g, ''))}
              />
            </FormField>
          ) : null}
          {source === 'task' ? (
            <FormField label="Task" hint="Its commits in this repository are analysed (at most 50)">
              <Select
                value={taskId}
                placeholder={tasks.isLoading ? 'Loading tasks…' : 'Choose a task'}
                options={(tasks.data?.items ?? []).map((task) => ({
                  value: task.id,
                  label: `${task.key} ${task.title}`,
                }))}
                onChange={(event) => setTaskId(event.target.value)}
              />
            </FormField>
          ) : null}
          <div>
            <Button
              type="submit"
              variant="primary"
              loading={request.isPending}
              disabled={!input || request.isPending}
              disabledReason={
                request.isPending ? 'The analysis is being requested' : 'Fill in what to analyse'
              }
            >
              Analyse
            </Button>
          </div>
          {request.isError ? <RequestError error={request.error} /> : null}
        </form>
      </Card>
      {analysisId ? <AnalysisView analysisId={analysisId} /> : null}
      <RecentAnalyses current={analysisId} />
    </div>
  );
}

function RequestError({ error }: { error: unknown }) {
  if (classifyProblem(error) === 'invalid') {
    return (
      <Alert tone="danger" title="The request was not accepted">
        Branch, tag and commit names may use letters, digits and . _ / @ ~ ^ - only, and must not
        start with a dash.
      </Alert>
    );
  }
  return <ProblemNotice error={error} size="sm" />;
}
